/**
 * paletteFill — safe palette->slice color lookup for the static preview SVGs.
 *
 * WHY THIS EXISTS (QA-44, Kelly, 2026-07-30, [Web] web-stg):
 * The wheel-wizard preview (`assets/icons/WheelPreview.jsx`) is a hand-exported
 * SVG with a HARD-CODED 20 slices, each painted `fill={colors[i]?.hex}`. The
 * palette that feeds it comes from the AI generator, which is ASKED for 20
 * colors but is not guaranteed to return 20 — Kelly's generation came back
 * with 19. Slot 19 then evaluated to `undefined`, React dropped the `fill`
 * attribute entirely, and the path INHERITED `fill="none"` from the root
 * `<svg>` — so that slice painted nothing and read as a white/blank wedge that
 * was not in her palette.
 *
 * It also produced the second half of her report. Adjacent slices in that
 * export OVERLAP each other by design (measured: the neighboring slice covers
 * 5.64% of slot 19's own area) and the overlap is normally hidden UNDER the
 * next slice's fill. With slot 19 unpainted, the neighbour's tail was exposed
 * and slot 19's own 2px white stroke cut across it — the thin blue line
 * "overhanging" the blank wedge. One root cause, both symptoms.
 *
 * THE FIX: wrap the index modulo the palette length, which is exactly what the
 * REAL wheel already does when a palette is shorter than the segment count
 * (`useSegmentSetting.js` getColor: `index % selectedWheelPalette.colors.length`).
 * This is not a new behavior — it is the existing behavior, applied to the
 * preview that was missing it.
 *
 * Deliberately unchanged: when there is NO palette at all the helper returns
 * `undefined`, so an un-generated preview keeps rendering exactly as it does
 * today (unpainted) rather than suddenly showing invented colors.
 */

/**
 * Normalize a palette into a plain array of hex strings.
 * Accepts the app's palette shape (`[{ hex: "#RRGGBB" }, ...]`) and a plain
 * array of hex strings; drops null/blank entries.
 *
 * @param {Array<{hex?: string}|string>} [colors]
 * @returns {string[]}
 */
export function normalizePalette(colors) {
  if (!Array.isArray(colors)) return [];
  return colors
    .map((c) => (typeof c === "string" ? c : c && c.hex))
    .filter((hex) => typeof hex === "string" && hex.trim() !== "");
}

/**
 * Colour for slice `index`, wrapping when the palette is shorter than the
 * number of slices. Returns `undefined` for an empty/absent palette.
 *
 * @param {Array<{hex?: string}|string>} [colors]
 * @param {number} index
 * @returns {string|undefined}
 */
export function paletteFillAt(colors, index) {
  const list = normalizePalette(colors);
  if (list.length === 0) return undefined;
  const i = Number.isFinite(index) && index >= 0 ? Math.floor(index) : 0;
  return list[i % list.length];
}

export default paletteFillAt;
